/* eslint-disable @typescript-eslint/no-explicit-any */
import config, { getLabelForCountry } from '../../../config'
import moment from 'moment'
import * as Constants from '../../base/util/constants'
import BaseModel from './BaseModel'
import orgamax from 'ampersand-app'
import { IPositionPriceItem, PositionPriceItem, IPositionItem, PositionItem } from './PositionItem'
import { Column } from './Column'
import { mapValues, isUndefined, omitBy } from 'lodash'
import { Customer, CustomerData, Contact, CustomerKind } from './Customer'
import { Supplier, SupplierContact, SupplierData } from './Supplier'
import lang from '@/lang'
import { IRecurringInvoiceModel } from './RecurringInvoiceModel'
import { isVat1, isVat2, vatCodesForUserByDate } from '@/modules/transactionV2/util/vat'
import { SpecialPositionTypes, PositionTypes } from './PositionTypes'
import Decimal from 'decimal.js'
import { ITenantContactPerson } from '@/modules/tenantContactPerson/tenantContactPersonService'
import { ITenantBankAccount } from '@/modules/tenantBankAccount/tenantBankAccountService'
import { DirectDebitTransaction } from './DirectDebitTransaction'
import { Address } from './Address'

const {
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_DEPOSIT_INVOICE,
  TRANSACTION_TYPE_CLOSING_INVOICE,
} = Constants.transactionTypes

const statesWithDraft = [
  TRANSACTION_TYPE_INVOICE,
  TRANSACTION_TYPE_DEPOSIT_INVOICE,
  TRANSACTION_TYPE_CLOSING_INVOICE,
]

export enum PriceKind {
  Gross = 'gross',
  Net = 'net'
}

interface Texts {
  introduction: string;
  conclusion: string;
}

export interface PayConditionData {
  name: string;
  invoiceText: string;
  offerText: string;
  orderText: string;
  discountPercentage: number;
  discountDays: number;
  dueDays: number;
  isBasic: boolean;
  dueEditable: boolean;
}
export interface DeliveryConditionData {
  name: string;
  text: string;
  deliveryDays: number;
}

enum InfoSectionFieldTypeEnum {
  string = 'string',
  number = 'number',
  date = 'date',
  dateRange = 'dateRange'
}

interface IInfoSectionField {
  name: string;
  label: string;
  active: boolean;
  required: boolean;
  type?: InfoSectionFieldTypeEnum;
}

export class InfoSectionField extends BaseModel<IInfoSectionField> implements IInfoSectionField {
  name: string
  label: string
  active: boolean
  required: boolean
}

interface IInfoSectionCustomField {
  label: string;
  value: string;
  active: boolean;
}

export class InfoSectionCustomField extends BaseModel<IInfoSectionField> implements IInfoSectionCustomField {
  label: string
  value: string
  active: boolean
}

export interface TransactionBaseInterface {
  useLaborShare: boolean
  id: string
  state: string
  type: string
  number: string
  date: Date
  beginDate: Date
  dueToDate: Date
  lockedAt: Date
  notes: string
  paidDate: Date
  printDate: Date
  title: string
  texts: Texts
  tags: string[] | object[]

  payConditionId: string | undefined
  payConditionData: PayConditionData | undefined
  deliveryConditionId: string | undefined
  deliveryConditionData: DeliveryConditionData | undefined

  addressId: string
  customerId: string
  supplierId: string
  contactPersonId: number
  customerData: CustomerData
  customerContactPersons: Contact[]
  supplierData: SupplierData
  supplierContactPersons: SupplierContact[]
  discount: number | undefined

  smallBusiness: boolean
  smallBusinessText: string
  letterPaperSettingId: string
  letterPaperLayoutId: string
  letterPaperPageId: string
  letterNumerationId: string
  shippingType: string
  shippingId: string
  shippingTrackingUrl: string

  infoSectionFields: InfoSectionField[]
  infoSectionCustomFields: InfoSectionCustomField[]

  columns: Column[]
  columnsChanged: boolean

  positions: IPositionItem[]
  payments: any[]
  projectModel: object

  vat: { [key: string]: number }
  totalDiscountNet: number | undefined
  totalDiscountGross: number | undefined
  totalSurchargeNet: number | undefined
  totalSurchargeGross: number | undefined
  subtotalNet: number | undefined
  subtotalGross: number | undefined

  textModuleId?: number | string
  relatedTransactions?: any[]
  deliveryAddress?: Address | undefined
  useCustomerAddress?: boolean | undefined
  relatedOfferNumber?: string | null
  relatedOrderNumber?: string | null

  recurringInvoiceSettings?: IRecurringInvoiceModel
  eInvoiceSettings?: {
    tenantContactPerson: ITenantContactPerson
    tenantBankAccount: ITenantBankAccount
    generateEInvoiceActive: boolean
    buyerInformation: {
      number?: string
      name?: string
      phone?: string
      email?: string
    }
    invoicePeriodInformation: {
      startDate?: string
      endDate?: string
    }
    additionalInvoiceInformation: {
      projectReference?: string
      projectName?: string
      contractName?: string
      contractReference?: string
      purchaseOrderReference?: string
      salesOrderReference?: string
      receivingAdviceReference?: string
      despatchAdviceReference?: string
      tenderReference?: string
      objectIdentifier?: string
      accountingReference?: string
      globalIdentifierSeller?: string
      globalIdentifierScheme?: string
      legalRegistrationIdentifier?: string
      legalRegistrationScheme?: string
    }
  }

  setCustomer(customer: Customer, useDeliveryAddress?: boolean, assign?: boolean): Partial<TransactionBaseInterface>
  setSupplier(supplier: Supplier, assign?: boolean): Partial<TransactionBaseInterface>
  clone(): Partial<TransactionBaseInterface>
}

export class TransactionBase implements TransactionBaseInterface {
  useLaborShare: boolean
  id: string
  state: string
  type: string
  number: string
  date: Date
  beginDate: Date
  dueToDate: Date
  lockedAt: Date
  notes: string
  paidDate: Date
  printDate: Date
  title: string
  texts: Texts
  tags: string[] | object[]

  payConditionId: string | undefined
  payConditionData: PayConditionData | undefined
  directDebitTransaction?: DirectDebitTransaction
  deliveryConditionId: string | undefined
  deliveryConditionData: DeliveryConditionData | undefined

  addressId: string
  customerId: string
  supplierId: string
  contactPersonId: number
  customerData: CustomerData
  customerContactPersons: Contact[]
  supplierData: SupplierData
  supplierContactPersons: SupplierContact[]
  discount: number | undefined

  smallBusiness: boolean
  smallBusinessText: string
  letterPaperSettingId: string
  letterPaperLayoutId: string
  letterPaperPageId: string
  letterNumerationId: string
  shippingType: string
  shippingId: string
  shippingTrackingUrl: string

  infoSectionFields: InfoSectionField[]
  infoSectionCustomFields: InfoSectionCustomField[]

  columns: Column[]
  columnsChanged: boolean

  positions: IPositionItem[]
  payments: any[]

  vat: { [key: string]: number }
  totalDiscountNet: number | undefined
  totalDiscountGross: number | undefined
  totalSurchargeNet: number | undefined
  totalSurchargeGross: number | undefined
  subtotalNet: number | undefined
  subtotalGross: number | undefined

  textModuleId?: number | string

  recurringInvoiceSettings?: IRecurringInvoiceModel
  eInvoiceSettings?: TransactionBaseInterface['eInvoiceSettings']
  projectModel: object
  relatedTransactions?: any[]
  relatedOfferNumber?: string | null
  relatedOrderNumber?: string | null

  // props

  get isPerson(): boolean {
    return this.customerData?.kind === CustomerKind.Person
  }

  get displayNumber(): string {
    if (statesWithDraft.includes(this.type) && this.state === Constants.invoiceStates.DRAFT) {
      return lang.draft
    }

    return this.number
  }

  constructor(options: { customer?: Customer, [key: string]: any } = {}, PositionItemClass: new (data, p) => PositionItem = PositionItem) {
    Object.assign(this, options)

    if (this.positions?.some(row => !(row instanceof PositionItemClass))) {
      this.positions = ((options.positions || []) as Array<IPositionItem>).map(row => row instanceof PositionItemClass ? row : new PositionItemClass(row, this))
    }
  }

  setCustomer(customer: Customer, useDeliveryAddress = false, assign = true): Partial<TransactionBaseInterface> {
    const data = getDataFromCustomer({
      ...customer,
      id: customer.id || this.customerId,
      payConditionId: customer.payConditionId || this.payConditionId,
      deliveryConditionId: customer.deliveryConditionId || this.deliveryConditionId,
      discount: customer.discount || this.discount,
    }, useDeliveryAddress)
    if (assign) {
      Object.assign(this, data)
    }

    return omitBy(data, isUndefined) as Partial<TransactionBaseInterface>
  }

  setSupplier(supplier: Supplier, assign = true): Partial<TransactionBaseInterface> {
    const data = getDataFromSupplier({
      ...supplier,
      id: supplier.id || this.supplierId,
      discount: supplier.discount || this.discount,
    })
    if (assign) {
      Object.assign(this, data)
    }

    return omitBy(data, isUndefined) as Partial<TransactionBaseInterface>
  }

  clone(): Partial<TransactionBaseInterface> {
    return new TransactionBase({ ...this })
  }
}

export function getDataFromCustomer(customer: Customer, useDeliveryAddress = false): Partial<TransactionBaseInterface> {
  const {
    id: customerId,
    addressId,
    number,
    vatNumber,
    netPriceAsDefault,
    contactPersons,
    deliveryConditionId,
    discount,
    payConditionId,
    contact,
    notesAlert,
    notes,
    email,
    buyerReference,
    customerOwnSupplierNumber,
    customerReferenceNumber,
  } = customer

  // default address data
  let city = customer.city || ''
  let zipCode = customer.zipCode || ''
  let street = customer.street || ''
  let countryIso = customer.countryIso || config.defaultCountryAsISO2
  let kind = customer.kind
  let salutation = customer.salutation
  let title = customer.title
  let name = customer.name
  let firstName = customer.firstName
  let lastName = customer.lastName
  let companyName = customer.companyName
  let companyNameAffix = customer.companyNameAffix

  // if transaction is created via customer detail view
  if (customer?.customerDefaultAddress?.deliveryAddress && useDeliveryAddress) {
    const deliveryAddress = customer?.customerDefaultAddress?.deliveryAddress
    countryIso = deliveryAddress.countryIso
    zipCode = deliveryAddress.zipCode
    city = deliveryAddress.city
    street = deliveryAddress.street
    kind = deliveryAddress.kind === CustomerKind.Person ? CustomerKind.Person : CustomerKind.Company
    salutation = deliveryAddress.salutation
    title = deliveryAddress.title
    name = deliveryAddress.name
    firstName = deliveryAddress.firstName
    lastName = deliveryAddress.lastName
    companyName = deliveryAddress.companyName
    companyNameAffix = deliveryAddress.companyNameAffix
  } else if (customer?.customerDefaultAddress?.billingAddress) {
    const billingAddress = customer?.customerDefaultAddress?.billingAddress
    countryIso = billingAddress.countryIso
    zipCode = billingAddress.zipCode
    city = billingAddress.city
    street = billingAddress.street
  }

  const countryData = {
    iso: countryIso,
    label: countryIso ? getLabelForCountry(countryIso).label : '',
  }

  const defaultCustomerData = {
    kind,
    street,
    zipCode,
    city,
    country: countryData.label,
    countryIso: countryData.iso,
    name,
    email: email || '',
  }

  let customerData = {
    number,
    netPriceAsDefault,
    vatNumber,
    notesAlert,
    notes,
  } as Partial<CustomerData>

  switch (kind) {
    case CustomerKind.Person:
      customerData = Object.assign(defaultCustomerData, customerData, {
        salutation,
        title,
        firstName,
        lastName,
        name: `${firstName || ''} ${customer.lastName || ''}`.trim(),
      })
      break
    case CustomerKind.Company:
      customerData = Object.assign(defaultCustomerData, customerData, {
        companyName,
        companyNameAffix,
        buyerReference,
        customerOwnSupplierNumber,
        customerReferenceNumber,
        name: `${companyName || ''} ${companyNameAffix || ''}`.trim(),
      })
  }

  const data = {
    customerId,
    addressId,
    discount: discount || 0.00,
    payConditionId,
    customerData,
    deliveryConditionId,
  } as Partial<TransactionBaseInterface>

  const getContactPersons = (contactPersons) => {
    if (!contactPersons) {
      return []
    }
    if (contactPersons.models) {
      return contactPersons.models
    }
    if (Array.isArray(contactPersons)) {
      return contactPersons
    }
    return [contactPersons]
  }
  const contactPersonModels = getContactPersons(contactPersons)

  if ((contact || contactPersonModels.length > 0) && kind === CustomerKind.Company) {
    const contactPersonModel = contact || contactPersonModels[0]
    Object.assign(data, {
      customerContactPersons: contactPersonModels,
    })
    Object.assign(data.customerData, {
      contact: {
        salutation: contactPersonModel.salutation,
        title: contactPersonModel.title,
        firstName: contactPersonModel.firstName,
        lastName: contactPersonModel.lastName,
        id: contactPersonModel.id,
      },
    })
  }

  // this.customerData.addressId = data.addressId

  data.customerData = omitBy(data.customerData, isUndefined) as CustomerData
  return omitBy(data, isUndefined) as Partial<TransactionBaseInterface>
}

export function getDataFromSupplier(supplier: Supplier): Partial<TransactionBaseInterface> {
  const {
    id: supplierId,
    defaultBillingAddressId,
    number,
    contactPersons,
    discount,
    contact,
    notesAlert,
    notes,
    email,
    customerReference,
  } = supplier

  // default address data
  let city = supplier.city || ''
  let zipCode = supplier.zipCode || ''
  let street = supplier.street || ''
  let countryIso = supplier.countryIso || config.defaultCountryAsISO2
  let name = supplier.name

  // if transaction is created via supplier detail view
  if (supplier?.defaultBillingAddressId && supplier?.addresses?.length) {
    const address = supplier?.addresses?.find(address => address.id === supplier?.defaultBillingAddressId && address.isDefault)
    countryIso = address.countryIso
    zipCode = address.zipCode
    city = address.city
    street = address.street
    name = address.name
  }

  const countryData = {
    iso: countryIso,
    label: countryIso ? getLabelForCountry(countryIso).label : '',
  }

  const defaultSupplierData = {
    street,
    zipCode,
    city,
    country: countryData.label,
    countryIso: countryData.iso,
    name,
    email: email || '',
  }

  const supplierData = Object.assign(defaultSupplierData, {
    number,
    notesAlert,
    notes,
    customerReference,
  } as Partial<SupplierData>)

  const data = {
    supplierId,
    defaultBillingAddressId,
    discount: discount || 0.00,
    supplierData,
  } as Partial<TransactionBaseInterface>

  const getContactPersons = (contactPersons) => {
    if (!contactPersons) {
      return []
    }
    if (contactPersons.models) {
      return contactPersons.models
    }
    if (Array.isArray(contactPersons)) {
      return contactPersons
    }
    return [contactPersons]
  }
  const contactPersonModels = getContactPersons(contactPersons)

  if (contact || contactPersonModels.length > 0) {
    const contactPersonModel = contact || contactPersonModels[0]
    Object.assign(data, {
      supplierContactPersons: contactPersonModels,
    })
    Object.assign(data.supplierData, {
      contact: {
        salutation: contactPersonModel.salutation,
        title: contactPersonModel.title,
        firstName: contactPersonModel.firstName,
        lastName: contactPersonModel.lastName,
        id: contactPersonModel.id,
      },
    })
  }

  data.supplierData = omitBy(data.supplierData, isUndefined) as SupplierData
  return omitBy(data, isUndefined) as Partial<TransactionBaseInterface>
}

export interface TotalTransactionInterface extends TransactionBaseInterface {
  useVerticalCalculation: boolean
  priceKind: PriceKind
  totalNet: number
  totalGross: number
  totalGrossProfit: number
  discount: number
}

export class TotalTransaction extends TransactionBase implements TotalTransactionInterface {
  useVerticalCalculation: boolean
  priceKind: PriceKind
  totalNet: number
  totalGross: number
  totalGrossProfit: number
  discount: number
  positions: IPositionPriceItem[]
  projectModel: object
  relatedTransactions?: any[]

  constructor(options) {
    super(options, PositionPriceItem)

    if (orgamax.user.isSmallBusiness) {
      this.priceKind = PriceKind.Net
    }
  }

  clone(): Partial<TransactionBaseInterface> {
    return new TotalTransaction({ ...this })
  }

  get getVatDate(): string {
    return moment(this.date).format(config.dateFormat.client)
  }

  get IsNet(): boolean {
    return this.priceKind === PriceKind.Net
  }
}

export function calculate(transaction: TransactionBaseInterface, rows: IPositionItem[] = []) {
  if (!(transaction instanceof TotalTransaction)) {
    return {}
  }
  if (transaction.totalNet === undefined) {
    return {}
  }

  const positions = rows.filter(row => !(row.metaData.isOptional || row.metaData.isAlternative))

  const vatList = vatCodesForUserByDate(transaction.getVatDate, orgamax)

  const calculatePositionValuesBasedOnNet = (sumsObj, position, index, positions) => {
    const { vatPercent } = position
    const isVatStandard = isVat1(vatPercent, vatList)
    const isVatReduced = isVat2(vatPercent, vatList)
    if (transaction.useVerticalCalculation) {
      if (isVatStandard || isVatReduced) {
        if (!sumsObj.vat[vatPercent]) {
          sumsObj.vat[vatPercent] = new Decimal(position.totalNetAfterDiscount)
        } else {
          sumsObj.vat[vatPercent] = new Decimal(sumsObj.vat[vatPercent]).plus(position.totalNetAfterDiscount)
        }
      }

      if (index === positions.length - 1) {
        Object.keys(sumsObj.vat).forEach(vatKey => {
          sumsObj.vat[vatKey] = new Decimal(sumsObj.vat[vatKey]).times((new Decimal(vatKey).dividedBy(100)))
          const isVatStandard = isVat1(vatKey, vatList)
          const isVatReduced = isVat2(vatKey, vatList)
          if (isVatStandard) {
            sumsObj.vatStandard = new Decimal(sumsObj.vatStandard || 0).plus(sumsObj.vat[vatKey])
          } else if (isVatReduced) {
            sumsObj.vatReduced = new Decimal(sumsObj.vatReduced || 0).plus(sumsObj.vat[vatKey])
          }
        })
      }
    } else {
      const totalNetAfterDiscountVat = new Decimal(position.totalGrossAfterDiscount).minus(position.totalNetAfterDiscount)
      if (totalNetAfterDiscountVat !== 0) {
        if (!sumsObj.vat[vatPercent]) {
          sumsObj.vat[vatPercent] = totalNetAfterDiscountVat
        } else {
          sumsObj.vat[vatPercent] = new Decimal(sumsObj.vat[vatPercent]).plus(totalNetAfterDiscountVat)
        }
        if (isVatStandard) {
          sumsObj.vatStandard = new Decimal(sumsObj.vatStandard || 0).plus(totalNetAfterDiscountVat)
        } else if (isVatReduced) {
          sumsObj.vatReduced = new Decimal(sumsObj.vatReduced || 0).plus(totalNetAfterDiscountVat)
        }
      }
    }

    const sumNet = new Decimal(position.totalNetAfterDiscount)

    sumsObj.net = new Decimal(sumsObj.net).plus(sumNet)
    if (index === positions.length - 1) {
      sumsObj.gross = new Decimal(sumsObj.vatStandard).plus(sumsObj.vatReduced).plus(sumsObj.net)
    }

    return sumsObj
  }

  const calculatePositionValuesBasedOnGross = (sumsObj, position, index, positions) => {
    const { vatPercent } = position
    const isVatStandard = isVat1(vatPercent, vatList)
    const isVatReduced = isVat2(vatPercent, vatList)
    if (transaction.useVerticalCalculation) {
      if (isVatStandard || isVatReduced) {
        if (!sumsObj.vat[vatPercent]) {
          sumsObj.vat[vatPercent] = new Decimal(position.totalGrossAfterDiscount)
        } else {
          sumsObj.vat[vatPercent] = new Decimal(sumsObj.vat[vatPercent]).plus(position.totalGrossAfterDiscount)
        }
      }

      if (index === positions.length - 1) {
        Object.keys(sumsObj.vat).forEach(vatKey => {
          sumsObj.vat[vatKey] = new Decimal(sumsObj.vat[vatKey])
            .minus(new Decimal(sumsObj.vat[vatKey])
              .dividedBy((new Decimal(vatKey).dividedBy(100).plus(1))))
          const isVatStandard = isVat1(vatKey, vatList)
          const isVatReduced = isVat2(vatKey, vatList)
          if (isVatStandard) {
            sumsObj.vatStandard = new Decimal(sumsObj.vatStandard || 0).plus(sumsObj.vat[vatKey])
          } else if (isVatReduced) {
            sumsObj.vatReduced = new Decimal(sumsObj.vatReduced || 0).plus(sumsObj.vat[vatKey])
          }
        })
      }
    } else {
      const totalGrossAfterDiscountVat = new Decimal(position.totalGrossAfterDiscount).minus(position.totalNetAfterDiscount)
      if (totalGrossAfterDiscountVat !== 0) {
        if (!sumsObj.vat[vatPercent]) {
          sumsObj.vat[vatPercent] = totalGrossAfterDiscountVat
        } else {
          sumsObj.vat[vatPercent] = new Decimal(sumsObj.vat[vatPercent]).plus(totalGrossAfterDiscountVat)
        }
        if (isVatStandard) {
          sumsObj.vatStandard = new Decimal(sumsObj.vatStandard || 0).plus(totalGrossAfterDiscountVat)
        } else if (isVatReduced) {
          sumsObj.vatReduced = new Decimal(sumsObj.vatReduced || 0).plus(totalGrossAfterDiscountVat)
        }
      }
    }

    const sumGross = new Decimal(position.totalGrossAfterDiscount)

    sumsObj.gross = new Decimal(sumsObj.gross).plus(sumGross)
    if (index === positions.length - 1) {
      sumsObj.net = new Decimal(sumsObj.gross).minus(sumsObj.vatReduced).minus(sumsObj.vatStandard)
    }

    return sumsObj
  }

  const calculationCallback = (transaction.priceKind === PriceKind.Net) ? calculatePositionValuesBasedOnNet : calculatePositionValuesBasedOnGross

  const calculateDiscountedSum = (calcSum, totalDiscountSum, totalSurchargeSum, subtotalSum) => {
    return new Decimal(calcSum)
      .times(new Decimal(1)
        .minus((new Decimal(totalDiscountSum).minus(totalSurchargeSum)).dividedBy(subtotalSum)))
  }

  const sums = positions
    .filter(position => ![...SpecialPositionTypes, PositionTypes.TOTAL_DISCOUNT, PositionTypes.TOTAL_SURCHARGE].includes(position.metaData.type))
    .reduce(calculationCallback, {
      net: new Decimal(0),
      vatReduced: new Decimal(0),
      vatStandard: new Decimal(0),
      vat: {},
      gross: new Decimal(0),
    })

  const calculatePositionSum = (positions, positionType) => {
    const validPositions = positions.filter(position => position.metaData.type === positionType && position.unit && position.amount)
    if (!validPositions.length) {
      return new Decimal(0)
    }
    return validPositions.reduce((sum, position) => {
      const { unit, amount } = position
      if (unit === 'percent') {
        switch (positionType) {
          case PositionTypes.TOTAL_DISCOUNT:
            sum = new Decimal(sum)
              .plus((new Decimal(transaction.priceKind === PriceKind.Net ? sums.net : sums.gross).minus(sum))
                .times(new Decimal(amount).dividedBy(100)))
            break
          case PositionTypes.TOTAL_SURCHARGE:
            sum = new Decimal(sum)
              .plus((new Decimal(transaction.priceKind === PriceKind.Net ? sums.net : sums.gross).plus(sum))
                .times(new Decimal(amount).dividedBy(100)))
            break
        }
      } else {
        sum = new Decimal(sum).plus(new Decimal(amount))
      }
      return sum
    }, new Decimal(0))
  }

  sums.totalDiscount = calculatePositionSum(positions, PositionTypes.TOTAL_DISCOUNT)
  sums.totalSurcharge = calculatePositionSum(positions, PositionTypes.TOTAL_SURCHARGE)

  let totalDiscountNet = new Decimal(0)
  let totalSurchargeNet = new Decimal(0)
  let totalDiscountGross = new Decimal(0)
  let totalSurchargeGross = new Decimal(0)

  if (transaction.priceKind === PriceKind.Net) {
    totalDiscountNet = new Decimal(sums.totalDiscount)
    totalSurchargeNet = new Decimal(sums.totalSurcharge)
    totalDiscountGross = sums.net !== 0 ? new Decimal(sums.totalDiscount).times(sums.gross).dividedBy(sums.net) : new Decimal(0)
    totalSurchargeGross = sums.net !== 0 ? new Decimal(sums.totalSurcharge).times(sums.gross).dividedBy(sums.net) : new Decimal(0)
  } else {
    totalDiscountNet = sums.gross !== 0 ? new Decimal(sums.totalDiscount).times(sums.net).dividedBy(sums.gross) : new Decimal(0)
    totalSurchargeNet = sums.gross !== 0 ? new Decimal(sums.totalSurcharge).times(sums.net).dividedBy(sums.gross) : new Decimal(0)
    totalDiscountGross = new Decimal(sums.totalDiscount)
    totalSurchargeGross = new Decimal(sums.totalSurcharge)
  }

  if (sums.net !== 0 && sums.gross !== 0 && (sums.totalDiscount || sums.totalSurcharge)) {
    Object.keys(sums.vat).map((vatPercent) => {
      sums.vat[vatPercent] = calculateDiscountedSum(sums.vat[vatPercent], totalDiscountNet, totalSurchargeNet, sums.net)
    })
    sums.vatReduced = calculateDiscountedSum(sums.vatReduced, totalDiscountNet, totalSurchargeNet, sums.net)
    sums.vatStandard = calculateDiscountedSum(sums.vatStandard, totalDiscountNet, totalSurchargeNet, sums.net)
  }

  // TODO: research the deeper meaning for transaction. looks like a workaround for an even older use case
  // result.vatAmountReduced = result.vat['7'] = new Decimal(sums.vatReduced).toDP(2).toNumber()
  // result.vatAmountStandard = result.vat['19'] = new Decimal(sums.vatStandard).toDP(2).toNumber()

  const totalNet = new Decimal(sums.net).minus(totalDiscountNet.toDP(2)).plus(totalSurchargeNet.toDP(2))
  const totalGross = new Decimal(sums.net).minus(totalDiscountNet.toDP(2)).plus(totalSurchargeNet.toDP(2)).plus(sums.vatReduced.toDP(2)).plus(sums.vatStandard.toDP(2))

  const convertToDecimal = (value) => {
    return new Decimal(value).toDP(2).toNumber()
  }

  return {
    subtotalNet: convertToDecimal(sums.net),
    subtotalGross: convertToDecimal(sums.gross),
    totalNet: convertToDecimal(totalNet),
    totalGross: convertToDecimal(totalGross),
    totalDiscountNet: convertToDecimal(totalDiscountNet),
    totalDiscountGross: convertToDecimal(totalDiscountGross),
    totalSurchargeNet: convertToDecimal(totalSurchargeNet),
    totalSurchargeGross: convertToDecimal(totalSurchargeGross),
    vatTotalAmountReduced: convertToDecimal(sums.vatReduced),
    vatTotalAmountStandard: convertToDecimal(sums.vatStandard),
    vat: mapValues(sums.vat, vatSum => convertToDecimal(vatSum)),
  }
}
